// KOA-5857 — Buffered-export confirmation modal.
//
// Firefox / Safari today don't expose the File System Access API, so
// the streaming-to-disk export path falls back to an Axios + Blob
// round-trip that buffers the FULL response in tab memory before
// saving. Multi-MB / multi-GB responses freeze or OOM the tab — no
// recovery short of reload.
//
// Until a proper server-side async export job ships, this modal is
// the honest UX: name the tradeoff, let the user decide. Threshold
// gating + state plumbing live in `use-table-editor-export.ts`; this
// component just renders the dialog when `bufferedExportConfirm` is
// non-null.
//
// Follows the table-editor's modal convention (mirror of the Cell
// Expander modal): KModal shell with kicker + title header and the
// shared KModalFooter slot — NOT inline buttons in the body.
// KModalFooter is where the one-size, one-casing, destructive-on-
// the-left decisions are already made for every dialog on this shell.

import React from "react";
import { KModal } from "../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../ui-kit/k-modal/k-modal-footer";
import { ExportFormat, IBufferedExportConfirm } from "./hooks/use-table-editor-export";

export interface IBufferedExportConfirmModalProps {
  confirm: IBufferedExportConfirm | null;
}

const FORMAT_LABEL: Record<ExportFormat, string> = {
  csv: "CSV",
  xlsx: "Excel (XLSX)",
  insert: "SQL INSERT",
  update: "SQL UPDATE",
  upsert: "SQL UPSERT"
};

const BufferedExportConfirmModal: React.FC<IBufferedExportConfirmModalProps> = (
  props: IBufferedExportConfirmModalProps
): JSX.Element | null => {
  const { confirm } = props;
  if (confirm === null) {
    return null;
  }
  const formatLabel: string = FORMAT_LABEL[confirm.format] ?? confirm.format.toUpperCase();
  return (
    <KModal
      showModal
      closeModal={confirm.onCancel}
      header={
        <span className="kuika-table-editor__cell-expander-header">
          <span className="kuika-table-editor__cell-expander-header-kicker" aria-hidden="true">
            Confirm export
          </span>
          <span className="kuika-table-editor__cell-expander-header-title">
            {confirm.rowCount.toLocaleString("en-US")} rows
          </span>
          <span className="kuika-table-editor__cell-expander-meta-pills">
            <span className="kuika-table-editor__cell-expander-pill">{formatLabel}</span>
          </span>
        </span>
      }
      width={480}
      footer={<KModalFooter onCancel={confirm.onCancel} submitLabel="Download anyway" onSubmit={confirm.onProceed} />}
    >
      <div className="kuika-table-editor__buffered-export-modal">
        <p className="kuika-table-editor__buffered-export-modal-detail">
          This export is large. Your browser needs to prepare the full file before saving, which may slow this tab or
          require a reload if it stops responding.
        </p>
        <p className="kuika-table-editor__buffered-export-modal-detail">
          If you regularly export tables this size, a recent version of Chrome or Edge handles it more smoothly.
        </p>
      </div>
    </KModal>
  );
};

export default BufferedExportConfirmModal;
